import styles from './support-grid.module.scss';
import { useState } from 'react';
import { ValueFormatterParams } from 'ag-grid-community';
import { useGetRequestNewHook, useTableConfigHook } from '../../requestUtils';
import { booleanHeader, Box, CreateHeaders, currencyHeader, dateHeader, HeaderSizeEnum, Icon, IUserAccount, minuteToHourConverter, PrimaryButton, SumRows, Tab, Tabs, textHeader, toCurrency, Typography } from '@frontend-workspace/react-shared-lib';
import AddNewUserDialog from '../add-new-user-dialog/add-new-user-dialog';
import AddImportDialog from '../add-import-dialog/add-import-dialog';
import CreditDialog from '../credit-dialog/credit-dialog';
import { ICustomerTransactions } from '../../_models/customer-transactions';
import { ISupportTicket } from '../../_models/customer-transactions';
import WebkuaTable from '../../webkua-table/webkua-table';
import { useTranslation } from 'react-i18next';
import { numberToNok } from '../../helpers';

/* eslint-disable-next-line */
export interface SupportGridProps {
  companyId: number,
  companyNumber: string,
  contractId: string,
  isGroupDeal: boolean,
  maxUsersOnContract: number,
  estimatedMinutesLeft: number,
  balance: number,
  usersOnContract: IUserAccount[],
  isLoading: boolean,
}

export function SupportGrid(props: SupportGridProps) {
  const { t } = useTranslation();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [addNewUser, setAddNewUser] = useState<boolean>(false);
  const [addImport, setAddImport] = useState<boolean>(false);
  const [credit, setCredit] = useState<boolean>(false);
  const tableConfig = useTableConfigHook("supportGrid");
  const tableConfigGroup = useTableConfigHook("supportGridGroupDeal");
  const tableConfigTransactions = useTableConfigHook("supportGridTransactions");

  const openAddNewUserDialog = () => setAddNewUser(true);
  const closeAddNewUserDialogDialog = () => setAddNewUser(false);
  const openAddImportDialog = () => setAddImport(true);
  const closeAddImportDialog = () => setAddImport(false);
  const openCreditDialog = () => setCredit(true);
  const closeCreditDialog = () => setCredit(false);

  const navigateToTabOne = () => setSelectedTab(0);
  const navigateToTabTwo = () => setSelectedTab(1);
  const navigateToTabThree = () => setSelectedTab(2);
  const { data: customerTransactions, getData: getTransactionData }: { data: ICustomerTransactions[], getData: () => void } = useGetRequestNewHook(`contracts/${props.contractId}/transactions`, [], [props.contractId]);
  const { data: supportTickets, getData: getSupportTickets }: { data: ISupportTicket[], getData: () => void } = useGetRequestNewHook(`supporttickets/${props.companyId}`, [], [props.companyId]);
  const minutesRenderer = (params: ValueFormatterParams) => minuteToHourConverter(params.value, "Ingen tid brukt");

  return <>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', marginBottom: '20px' }}>
      <Tabs
        value={selectedTab}
        variant="scrollable"
        scrollButtons="auto">
        <Tab label={t("supportGrid.usersOnTheAgreement")} onClick={navigateToTabOne} />
        <Tab label={t("supportGrid.transactions")} onClick={navigateToTabTwo} />
        <Tab label={t("supportGrid.tickets")} onClick={navigateToTabThree} />
      </Tabs>
    </Box>
    {selectedTab === 0 && <>
      <div className="d-flex">
        <div className="mb-3 mb-sm-0">
          <PrimaryButton
            variant="contained"
            onClick={() => openAddNewUserDialog()}>
            <Icon className="me-1" fontSize="small">add</Icon>{t("standard.changeUsers")}
          </PrimaryButton>
        </div>
      </div>

      <WebkuaTable
        isLoading={props.isLoading}
        headers={CreateHeaders([
          textHeader("firstName", t("standard.firstName"), HeaderSizeEnum.LARGE),
          textHeader("lastName", t("standard.lastName"), HeaderSizeEnum.LARGE),
          textHeader("email", t("standard.focusAccount"), HeaderSizeEnum.EXTRA_LARGE),
          textHeader("mobile", t("standard.phone"), HeaderSizeEnum.LARGE),
          textHeader("companyNumber", t("standard.companyNo"), HeaderSizeEnum.LARGE),
          textHeader("companyName", t("standard.company"), HeaderSizeEnum.EXTRA_LARGE),
          dateHeader("startDate", t("standard.startDate")),
          dateHeader("endDate", t("standard.terminationDate")),
        ])}
        data={props.usersOnContract}
        tableConfig={tableConfigGroup}
      />
    </>}
    {selectedTab === 1 && <>
      <div className="d-flex flex-row align-items-center flex-wrap mb-3 mb-xl-0">
        <div className='mb-3 mb-sm-0' style={{ width: '250px' }}>
          <Typography
            variant="h6"
            component="div"
            className=" me-4">
            {`${t("supportGrid.estimatedHourlyBalance")}: ${minuteToHourConverter(props.estimatedMinutesLeft, t("supportGrid.spent"))}`}
          </Typography>
          <Typography
            variant="h6"
            component="div"
            className="me-4">
            {`${t("supportGrid.amountBalance")}: ${toCurrency(props.balance)}`}
          </Typography>
        </div>
        <div className="mb-3 mb-sm-0 me-4">
          <PrimaryButton
            variant="contained"
            onClick={() => openAddImportDialog()}
            className='minButtonWidth'>
            <Icon className="me-1" fontSize="small">add</Icon>{t("standard.importTicket")}
          </PrimaryButton>
        </div>
        <div className="mb-3 mb-sm-0">
          <PrimaryButton
            variant="outlined"
            onClick={() => openCreditDialog()}
            className='minButtonWidth'>
            {t("standard.credit")}
          </PrimaryButton>
        </div>
      </div>
      <WebkuaTable
        headers={CreateHeaders([
          textHeader("transactionText", t("supportGrid.text"), HeaderSizeEnum.LARGE, { renderer: "agGroupCellRenderer" }),
          dateHeader("createdDate", t("supportGrid.caseDate")),
          dateHeader("transactionDate", t("supportGrid.transactionDate")),
          textHeader("ownedBy", t("supportGrid.ownedBy"), HeaderSizeEnum.MEDIUM),
          textHeader("customerName", t("standard.user"), HeaderSizeEnum.MEDIUM,),
          textHeader("minutesUsed", t("supportGrid.hours"), HeaderSizeEnum.MEDIUM, {
            renderer: minutesRenderer,
            summary: SumRows,
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          }),
          textHeader("priceInfo.minutePrice", t("supportGrid.price15minutes"), HeaderSizeEnum.MEDIUM, { renderer: (params: ValueFormatterParams) => params.value ? numberToNok(params.value?.toFixed(2) * 15) : t("standard.noPriceInformation") }),
          booleanHeader("userOnSupportAgreement", t("supportGrid.userOnSupportAgreement")),
          currencyHeader("amount", t("standard.amount"), HeaderSizeEnum.SMALL, {
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          }),
          currencyHeader("balance", t("supportGrid.balance"), HeaderSizeEnum.MEDIUM, {
            cellStyle: { justifyContent: 'right' },
            headerClass: "ag-right-aligned-header"
          })
        ])}
        tableConfig={tableConfigTransactions}
        data={customerTransactions}
        expandComponent={TransactionExpandComponent}

      />
    </>}
    {selectedTab === 2 && <>
      <WebkuaTable
        isLoading={props.isLoading}
        headers={CreateHeaders([
          textHeader("ticketId", t("supportGrid.ticketId")),
          textHeader("title", t("supportGrid.message"), HeaderSizeEnum.EXTRA_LARGE),
          textHeader("createdAt", t("supportGrid.createdAt"), HeaderSizeEnum.LARGE),
          textHeader("author", t("supportGrid.createdBy"), HeaderSizeEnum.LARGE),
          textHeader("status", t("supportGrid.status"), HeaderSizeEnum.LARGE),
        ])}
        data={supportTickets}
        tableConfig={tableConfigGroup}
      />
    </>}
    {addNewUser && <AddNewUserDialog
      open={!!addNewUser}
      onClose={closeAddNewUserDialogDialog}
      contractId={props.contractId}
      companyNumber={props.companyNumber}
      isGroupDeal={props.isGroupDeal}
      maxUsersOnContract={props.maxUsersOnContract}
      currentUsersPersonIds={props.usersOnContract.map(u => u.email)}
    />}
    <AddImportDialog
      open={!!addImport}
      onClose={closeAddImportDialog}
      contractId={props.contractId}
    />
    <CreditDialog
      open={!!credit}
      onClose={closeCreditDialog}
      getData={getTransactionData}
      contractId={props.contractId}
      balance={props.balance}
    />
  </>
}
function TransactionExpandComponent(props: { data: ICustomerTransactions }) {
  const priceInfo = props.data.priceInfo;
  const { t } = useTranslation();

  const tableConfig = useTableConfigHook("transactionExpand");
  if (!priceInfo) return <Box className="d-flex flex-column py-4 px-2">{t("standard.noPriceInformation")}</Box>;
  const disconnectedMinutesRenderer = (params: ValueFormatterParams) => minuteToHourConverter(params.value.toFixed(2), t("standard.noTimeSpent"));
  return (
    <Box className="d-flex flex-column py-4 px-2">
      <WebkuaTable
        headers={CreateHeaders([
          textHeader("minutePrice", t("supportGrid.price15minutes"), HeaderSizeEnum.LARGE, { renderer: (params: ValueFormatterParams) => params.value ? numberToNok(params.value?.toFixed(2) * 15) : t("standard.noPriceInformation") }),
          textHeader("minutes", "Minutter", HeaderSizeEnum.LARGE, { renderer: disconnectedMinutesRenderer }),
          currencyHeader("amount", t("standard.amount"), HeaderSizeEnum.LARGE),
        ])}
        data={priceInfo.prices}
        tableConfig={tableConfig}
        hidePagination
        disableSearch
      />
    </Box>
  );
}

export default SupportGrid;